import 'animate.css';
import 'react-toastify/dist/ReactToastify.css';
import Header from '@/components/Header/Header';
import TopNav from '@/components/Header/TopNav';
import Chat from '@/components/Chat/Chat';
import { ToastContainer } from 'react-toastify';
import ModalWrapper from '@/components/Modals/ModalWrapper';
import useUserStore from '@/stores/user.store';
import { useEffect, useRef } from 'react';
import { useTracker, useSubscription, socket } from '@/utils/hooks/use-subscription';
import { cryptoStore } from '@/stores/crypto.store';
import { modalStore } from '@/stores/modal.store';
import Script from 'next/script';
import GlobalTooltip from '@/components/Tooltip/GlobalTooltip';
import '@/utils/timesync';

const MainLayout = ({ children }: { children: React.ReactNode }) => {
    const { fetchUser, patchUser, isAuthed, snow } = useUserStore((state) => state);
    const { getWallets, getRates, getConfig } = cryptoStore((state) => state);
    const { sounds, setSound, hydrateSound } = modalStore((state) => state);

    useTracker();

    useEffect(() => {
        fetchUser();
        hydrateSound();
    }, []);
    useEffect(() => {
        if (isAuthed) {
            getRates().then();
            getWallets().then();
            getConfig().then();
        }
    }, [isAuthed]);

    // Debounced refetch — game-finish bursts (jackpot + coinflip + battle
    // finishing within the same second) shouldn't translate into a stampede
    // of /user requests.
    const refetchTimer = useRef<number | null>(null);
    const scheduleRefetch = () => {
        if (refetchTimer.current) window.clearTimeout(refetchTimer.current);
        refetchTimer.current = window.setTimeout(() => {
            refetchTimer.current = null;
            fetchUser();
        }, 250);
    };

    // Reattach the userUpdated listener whenever fetchUser/patchUser identities
    // change (Zustand normally keeps them stable, but if a parent rerender
    // re-creates the closure we want the new one). Re-registering also lets us
    // see fresh handlers after socket reconnect.
    useEffect(() => {
        const onUserUpdated = (data: any) => {
            const payload = data?.user ?? data;
            if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
                const keys = [
                    'experience',
                    'balanceUsd',
                    'balanceUsdHold',
                    'level',
                    'name',
                    'avatar',
                    'withdrawRestrictionText',
                ];
                const patch: Record<string, unknown> = {};
                for (const k of keys) {
                    if (payload[k] !== undefined) patch[k] = payload[k];
                }
                if (Object.keys(patch).length > 0) {
                    patchUser(patch);
                    return;
                }
            }
            fetchUser();
        };
        socket.on('userUpdated', onUserUpdated);
        return () => {
            socket.off('userUpdated', onUserUpdated);
        };
    }, [fetchUser, patchUser]);

    // Fallback: most XP gains happen on game finish. If the backend's
    // dedicated userUpdated emit ever stops, these keep the level fresh.
    useSubscription('jackpotFinished', () => scheduleRefetch()).then();
    useSubscription('coinflipFinished', () => scheduleRefetch()).then();
    useSubscription('casebattleFinished', () => scheduleRefetch()).then();
    useSubscription('tradeReceived', () => scheduleRefetch()).then();

    // Backend can force a session reset (e.g. auth method switch / ban) —
    // reload so the client picks up the new auth state.
    useSubscription('forceLogout', () => window.location.reload()).then();

    return (
        <div className="fluent-bg min-h-screen text-text_main">
            {snow && <Script src="/snow.js" />}
            <canvas className={'fixed w-screen h-screen z-50 pointer-events-none'} id={'canvas'} />
            <ToastContainer
                position="top-right"
                hideProgressBar
                closeOnClick={false}
                pauseOnHover={false}
                draggable={false}
                theme="dark"
                icon={false}
                toastClassName="fluent-toast"
            />
            <ModalWrapper />
            <GlobalTooltip />

            {/* Mobile header (xl:hidden) */}
            <Header isAuthed={isAuthed} setSound={setSound} sounds={sounds} />

            {/* Desktop: sticky horizontal top navigation (xl+) */}
            <TopNav />

            {/* Desktop: [main | chat] under the top nav; Mobile: single column under the header bar */}
            <div
                className={
                    'xl:grid xl:grid-cols-[minmax(0,1fr)_340px] xl:gap-3 xl:px-3 xl:pb-3 xl:pt-3 xl:items-start max-xl:pt-[104px]'
                }
            >
                <main className={'min-w-0 max-xl:px-3 max-xl:pb-3'}>{children}</main>
                <Chat />
            </div>
        </div>
    );
};

export default MainLayout;
